import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltip } from "@angular/material/tooltip";
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { addMinutes, addSeconds, differenceInMilliseconds, format, formatISO, isBefore, isWithinInterval, subHours, subSeconds, subYears } from 'date-fns';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ALL_ROLES, GalleryComponent, TagDisplayConfig } from 'src/app/components/shared/gallery/gallery.component';
import { Attachment } from 'src/app/interfaces/api/attachment';
import { Event } from 'src/app/interfaces/api/event';
import { Metric } from 'src/app/interfaces/api/metric';
import { Project } from 'src/app/interfaces/api/project';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { Unit } from 'src/app/interfaces/api/unit';
import { User } from 'src/app/interfaces/api/user';
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { EventService } from 'src/app/services/api/events/event.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { WebSocketService } from 'src/app/services/core/web-socket.service';
import { ButtonComponent, ChipComponent, SpinnerComponent } from 'ui-library';

@Component({
  selector: 'app-event-list',
  templateUrl: './event-list.component.html',
  styleUrls: ['./event-list.component.scss'],
  imports: [
    MatTabsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatMenuModule,
    CommonModule,
    FormsModule,
    TranslateModule,
    MatIconModule,
    NoDataComponent,
    ChipComponent,
    MatTooltip,
    ButtonComponent
  ],
  standalone: true
})

export class EventListComponent implements AfterViewInit, OnDestroy, OnInit, OnChanges {
  // Services
  private readonly metricService: MetricService = inject(MetricService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly eventService: EventService = inject(EventService);
  private readonly attachmentService: AttachmentService = inject(AttachmentService);
  private readonly socket: WebSocketService = inject(WebSocketService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly authService: AuthService = inject(AuthService);

  // Components API
  @Input() timelineData: any = {
    attachments: [],
    events: [],
    metrics: []
  };
  @Input() startDate = subYears(new Date(), 1).toISOString();
  @Input() endDate = new Date().toISOString();
  @Input() tabIndex: number = 0;
  @Input() eventTypes$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(["Generated event", "Flow event", "User defined", "Tracker log"]);
  @Input() metricTypes$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  @Input() trackerTypes$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  @Input() unitTypes$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  @Input() search!: BehaviorSubject<string>;
  @ViewChild("eventPaginator") eventPaginator!: MatPaginator;
  @ViewChild("metricPaginator") metricPaginator!: MatPaginator;
  @ViewChild("attachmentPaginator") attachmentPaginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  loading: Boolean = false;
  metricSub!: Subscription | null;
  eventSub!: Subscription | null;
  trackerSub!: Subscription | null;
  unitSub!: Subscription | null;
  metricTypes: string[] = [];
  eventTypes: string[] = [];
  trackerTypes: string[] = [];
  unitTypes: string[] = [];
  currentSearch: string = "";

  socketAttachmentAttached = false;
  socketAttachmentSub!: Subscription | null;
  socketEventSub!: Subscription | null;

  isLoadingEvents: Boolean = false;
  isLoadingMetrics: Boolean = false;
  isLoadingAttachements: Boolean = false;
  isRateLimitReached: Boolean = false;


  paginator: any = {
    pageIndex: 0,
    pageSize: 10
  };

  paginatorEventSbj: BehaviorSubject<any> = new BehaviorSubject({ ...this.paginator });
  paginatorMetricSbj: BehaviorSubject<any> = new BehaviorSubject({ ...this.paginator });
  paginatorAttachmentSbj: BehaviorSubject<any> = new BehaviorSubject({ ...this.paginator });

  events: Event[] = [];
  eventsCount: Number = 0;
  eventsData!: MatTableDataSource<Event>;
  eventsColumns: string[] = ['date', 'title', 'resources', 'zones', 'actions'];

  metrics: Metric[] = [];
  metricsCount: Number = 0;
  metricsData!: MatTableDataSource<Metric>;
  metricsColumns: string[] = ['date', 'title', 'payload', 'actions'];

  attachments: Attachment[] = [];
  attachmentsCount: Number = 0;
  attachmentsData!: MatTableDataSource<Attachment>;
  attachmentsColumns: string[] = ['date', 'picture', 'title', 'predictions', 'actions'];
  searching: boolean = false;

  trackersWithShowTrue: Tracker[] = [];
  currentProject: Project = {} as Project;
  user!: User;
  attachmentsMap: Map<string, Attachment[]> = new Map();
  destroy$ = new Subject<void>();

  tagDisplayConfig: TagDisplayConfig = {
    visibility: {
      crane: { ai: ALL_ROLES, manual: ALL_ROLES },
      zone: { ai: ALL_ROLES, manual: ALL_ROLES },
      action: { ai: ALL_ROLES, manual: ALL_ROLES },
      worker: { ai: ['dev', 'admin'], manual: ['dev', 'admin'] },
      risk: { ai: ['dev'], manual: ['dev', 'admin'] }
    }
  };

  payloadIconConfig: Record<string, { icon?: string; svgIcon?: string; color: string }> = {
    color: { icon: 'palette', color: 'primary' },
    startWeight: { svgIcon: 'shackle_full', color: 'terciary' },
    endWeight: { svgIcon: 'shackle_full', color: 'terciary' },
    weight: { svgIcon: 'shackle_full', color: 'terciary' },
    volume: { icon: 'water_drop', color: 'secondary' },
    duration: { icon: 'timer', color: 'danger' },
    lat: { icon: 'location_on', color: 'fuchsia' },
    lon: { icon: 'location_on', color: 'fuchsia' },
    z: { icon: 'trending_up', color: 'fuchsia' },
    report: { icon: 'description', color: 'primary' },
    concreting: { icon: 'description', color: 'teal' },
    action: { icon: 'label', color: 'teal' },
    persist: { icon: 'save', color: 'primary' },
    label: { icon: 'label', color: 'teal' },
    time: { icon: 'timer', color: 'danger' },
  };

  constructor() {
    let tabIndex = this.route.snapshot.queryParamMap.get('tab');
    if (tabIndex) this.tabIndex = parseInt(tabIndex);

  }

  async ngOnInit() {
    this.trackerSub = this.trackerTypes$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.trackerTypes = data;
    });

    this.projectService.currentProject.pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.currentProject = res
    })

    this.eventSub = this.eventTypes$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.eventTypes = data;
      if (this.tabIndex == 0)
        this.ngOnChanges();
    });

    this.metricSub = this.metricTypes$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.metricTypes = data;
      if (this.tabIndex == 1)
        this.ngOnChanges();
    });

    this.unitSub = this.unitTypes$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.unitTypes = data;
      if (this.tabIndex == 0)
        this.ngOnChanges();
    })
    // this.trackerTypes$.pipe(takeUntil(this.destroy$)).subscribe(data => {
    //   this.trackerTypes = data;
    // })
  }

  ngAfterViewInit(): void {
    // this.getEvents()
    // If the user changes the sort order, reset back to the first page.
    this.sort.sortChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.eventPaginator.pageIndex = 0);
    this.eventPaginator.page.pipe(takeUntil(this.destroy$)).subscribe(() => this.paginatorEventSbj.next(this.eventPaginator));
    this.metricPaginator.page.pipe(takeUntil(this.destroy$)).subscribe(() => this.paginatorMetricSbj.next(this.metricPaginator));
    this.attachmentPaginator.page.pipe(takeUntil(this.destroy$)).subscribe(() => this.paginatorAttachmentSbj.next(this.attachmentPaginator));

    this.paginatorEventSbj.pipe(takeUntil(this.destroy$)).subscribe(pgt => {
      this.getEvents();
    })
    this.paginatorMetricSbj.pipe(takeUntil(this.destroy$)).subscribe(pgt => {
      this.getMetrics();
    })

    this.paginatorAttachmentSbj.pipe(takeUntil(this.destroy$)).subscribe(pgt => {
      this.getAttachments();
    })

    // this.getEvents();
    // this.getMetrics();
    // this.getAttachments();

    this.search.pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result !== this.currentSearch) {
        this.currentSearch = result;
        switch (this.tabIndex) {
          case 0:
            this.getEvents();
            break;
          case 1:
            this.getMetrics();
            break;
          case 2:
            this.getAttachments();
            break;
        }
      }
    })

    this.initSocket();
  }

  ngOnChanges() {
    if (!this.startDate) this.startDate = subYears(new Date(), 1).toISOString();
    if (!this.endDate) this.endDate = new Date().toISOString();
    // console.trace("changes")
    this.onTabChanged(this.tabIndex)

  }

  ngOnDestroy(): void {

    if (this.socketEventSub) {
      this.socketEventSub.unsubscribe();
    }
    if (this.socketAttachmentSub) {
      this.socketAttachmentSub.unsubscribe();
    }

    if (this.metricSub)
      this.metricSub.unsubscribe();

    if (this.eventSub)
      this.eventSub.unsubscribe();

    if (this.trackerSub)
      this.trackerSub.unsubscribe();

    if (this.unitSub)
      this.unitSub.unsubscribe();

    this.socketEventSub = null;
    this.socketAttachmentSub = null;
    this.metricSub = null;
    this.eventSub = null;
    this.trackerSub = null;
    this.unitSub = null;

    this.destroy$.next();
    this.destroy$.complete();
  }

  getMetrics() {

    let paginator = this.paginatorMetricSbj.getValue();

    let query: any = { startDate: this.startDate, endDate: this.endDate }
    if (this.trackerTypes && this.trackerTypes.length) {
      query.trackerNames = this.trackerTypes;
    }
    if (this.metricTypes && this.metricTypes.length) {
      query.names = this.metricTypes;
    }

    if (this.currentSearch && this.currentSearch !== '') {
      this.metrics = [];
      this.updateTable();
      this.searching = true;

      this.metricPaginator.firstPage();

      this.metricService.getMetricsCount(query).pipe(takeUntil(this.destroy$)).subscribe(res => {
        this.metricsCount = res;
      })
      this.metricService.searchMetrics(this.currentSearch, { ...query, offset: paginator.pageIndex * paginator.pageSize, limit: paginator.pageSize })
        .pipe(takeUntil(this.destroy$)).subscribe(data => {
          this.handleMetrics(data);
          this.searching = false;
          //this.isLoadingMetrics = false;
        });
    } else {

      this.metricService.getMetricsCount(query).pipe(takeUntil(this.destroy$)).subscribe(res => {
        this.metricsCount = res;
      })
      this.metricService.getMetrics({ ...query, offset: paginator.pageIndex * paginator.pageSize, limit: paginator.pageSize })
        .pipe(takeUntil(this.destroy$)).subscribe(data => {
          this.handleMetrics(data);
          //this.isLoadingMetrics = false;
        });
    }
  }

  async handleMetrics(metrics: any[]) {
    let startDate: string = '', endDate: string = '';

    metrics.forEach((metric: any) => {
      metric.initTitle = metric.title;
      metric.title = metric.name + " | " + (metric.payload?.title || '');

      if (!startDate && metric.payload?.startDate)
        startDate = metric.payload.startDate;
      else if (startDate && metric.payload?.startDate && differenceInMilliseconds(new Date(startDate), new Date(metric.payload.startDate)) > 0)
        startDate = metric.payload.startDate;

      if (!endDate && metric.payload?.endDate)
        endDate = metric.payload.endDate;
      else if (endDate && metric.payload?.endDate && differenceInMilliseconds(new Date(endDate), new Date(metric.payload.endDate)) < 0)
        endDate = metric.payload.endDate

      if (metric.payload?.startDate) {
        metric.startDate = metric.payload.startDate;
        metric.endDate = metric.payload.endDate;
      } else {
        metric.startDate = metric.createdDate;
      }

      metric.payloadArray = [];

      if (metric.payload) {
        Object.keys(metric.payload).forEach(key => {
          if (key == "id" || key == "boxes" || key == "thumbnail" || key == "picture" || key == "startDate" || key == "endDate" || key == "title") {
            return;
          }

          let value = metric.payload[key];

          if (!value)
            value = "Not defined";

          if (key.toLocaleLowerCase().includes("date"))
            value = format(new Date(value), "dd/MM/yyyy HH:mm:ss");

          metric.payloadArray.push({ key, value });
        })
      }
    })

    if (endDate) {
      if (this.attachmentsMap.has(`${startDate}|${endDate}`)) {
        let attachments = this.attachmentsMap.get(`${startDate}|${endDate}`);

        if (attachments && attachments.length)
          this.attachToMetrics(metrics, attachments);

        this.metrics = metrics.map(this.parseEvent)
        this.updateTable();
      } else {
        this.attachmentService.getAttachments({ startDate, endDate }).pipe(takeUntil(this.destroy$)).subscribe((attachments: any[]) => {
          attachments.sort((a, b) => differenceInMilliseconds(new Date(a.createdDate), new Date(b.createdDate)) > 0 ? 1 : -1)
          // this.attachments = attachments;
          this.attachmentsMap.set(`${startDate}|${endDate}`, attachments);

          if (attachments && attachments.length)
            this.attachToMetrics(metrics, attachments);

          this.metrics = metrics.map(this.parseEvent)
          this.updateTable();
        })
      }

    } else {
      this.metrics = metrics.map(this.parseEvent)
      this.updateTable();
    }
  }

  private attachToMetrics(metrics: Metric[] = [], attachments: Attachment[] = []) {
    metrics.forEach((metric: any) => {
      metric.attachments = [];

      if (metric.startDate && metric.endDate) {
        attachments.forEach((a, i) => {
          let tracker = (a.tracker as any)?.name ? (a.tracker as any).name : a.tracker;
          let unit = (a.unit as any)?.name ? (a.unit as any).name : a.unit;

          if (isWithinInterval(new Date(a.createdDate), { start: new Date(metric.startDate), end: new Date(metric.endDate) }) &&
            ((metric.tracker == a.tracker) || (metric.unit == a.unit) || (metric.payload?.tracker && metric.payload.tracker == tracker) || (metric.payload?.unit && metric.payload.unit == unit))) {
            metric.attachments.push(a);
          }
        })
      }
    })
  }

  parseEvent(event: any) {
    let attachments: Attachment[] = [];

    if (event.endDate) {
      event.startDateText = format(new Date(event.startDate), "yyyy/MM/dd HH:mm:ss");
      event.endDateText = format(new Date(event.endDate), "yyyy/MM/dd HH:mm:ss");

      var ms = differenceInMilliseconds(new Date(event.endDate), new Date(event.startDate));
      var totalSeconds = Math.floor(ms / 1000);
      var hours = Math.floor(totalSeconds / 3600);
      var minutes = Math.floor((totalSeconds % 3600) / 60);
      var seconds = totalSeconds % 60;
      var s = `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

      event.duration = s;

    } else {
      event.date = format(new Date(event.startDate), "yyyy/MM/dd HH:mm:ss");

    }
    return event
  }
  getEvents() {
    //if (this.isLoadingEvents) return;
    // this.isLoadingEvents = true;

    let paginator = this.paginatorEventSbj.getValue();
    let query: any = { startDate: this.startDate, endDate: this.endDate }
    if (this.trackerTypes && this.trackerTypes.length) {
      query.trackerNames = this.trackerTypes;
    }
    if (this.unitTypes && this.unitTypes.length) {
      query.unitNames = this.unitTypes;
    }
    if (this.eventTypes && this.eventTypes.length) {
      query.types = this.eventTypes;
    }

    this.eventService.getEventsCount(query).pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.eventsCount = res;
    });

    if (this.currentSearch && this.currentSearch !== '') {
      this.events = [];
      this.updateTable();
      this.searching = true;

      this.eventPaginator.firstPage();

      this.eventService.searchEvents(this.currentSearch, { ...query, offset: paginator.pageIndex * paginator.pageSize, limit: paginator.pageSize })
        .pipe(takeUntil(this.destroy$)).subscribe(data => {
          // this.isLoadingEvents = false;
          this.handleEvents(data);
          this.searching = false;
        });

    } else {

      this.eventService.getEvents({ ...query, offset: paginator.pageIndex * paginator.pageSize, limit: paginator.pageSize })
        .pipe(takeUntil(this.destroy$)).subscribe(data => {
          //this.isLoadingEvents = false;
          this.handleEvents(data)
        });
    }
  }

  handleEvents(events: any[]) {
    let startDate: string = '', endDate: string = '';

    if (!events.length || this.areSameEvents(this.events, events)) {
      return;
    }

    if (events) {
      events.forEach(event => {
        event.initTitle = event.title;
        if (!startDate) startDate = String(event.startDate);
        else if (startDate && differenceInMilliseconds(new Date(startDate), new Date(String(event.startDate))) > 0) startDate = String(event.startDate);
        if (!endDate && event.endDate) endDate = String(event.endDate);
        else if (endDate && event.endDate && differenceInMilliseconds(new Date(endDate), new Date(String(event.endDate))) < 0) endDate = String(event.endDate);
      })
      if (endDate) {
        if (this.attachmentsMap.has(`${startDate}|${endDate}`)) {
          let attachments = this.attachmentsMap.get(`${startDate}|${endDate}`);
          if (attachments && attachments.length)
            this.attachToEvents(events, attachments);

          this.events = events.map(this.parseEvent)
          this.updateTable();
        } else {
          this.attachmentService.getAttachments({ startDate, endDate }).pipe(takeUntil(this.destroy$)).subscribe((attachments: any[]) => {
            attachments.sort((a, b) => differenceInMilliseconds(new Date(a.createdDate), new Date(b.createdDate)) > 0 ? 1 : -1);
            this.attachmentsMap.set(`${startDate}|${endDate}`, attachments);
            // this.attachments = attachments;
            if (attachments && attachments.length)
              this.attachToEvents(events, attachments);

            this.events = events.map(this.parseEvent)
            this.updateTable();
          })
        }

      } else {
        this.events = events.map(this.parseEvent)
        this.updateTable();
      }
    }
  }

  private areSameEvents(events: Event[], events2: Event[]) {
    if (events.length !== events2.length) return false;
    let arr1 = events.map((e: Event) => e.id);
    let arr2 = events2.map((e: Event) => e.id);
    return arr1.every((value: string) => arr2.includes(value));
  }

  private attachToEvents(events: Event[] = [], attachments: Attachment[] = []) {
    events.forEach(event => {
      event.attachments = [];
      if (event.startDate && event.endDate) {
        attachments.forEach((a, i) => {
          let tracker = (a.tracker as any)?.name ? (a.tracker as any).name : a.tracker;
          let unit = (a.unit as any)?.name ? (a.unit as any).name : a.unit;
          if (isWithinInterval(new Date(a.createdDate), { start: new Date(event.startDate), end: new Date(event.endDate!) }) &&
            ((event.trackerName && event.trackerName == tracker) || (event.unitName && event.unitName == unit))) {
            event.attachments?.push(a);
          }
        })
      }

    })
  }

  async getAttachments() {
    let query: any = { startDate: this.startDate, endDate: this.endDate }
    if (this.trackerTypes && this.trackerTypes.length) {
      query.trackerNames = this.trackerTypes;
    }

    if (this.currentSearch && this.currentSearch !== '') {
      this.attachments = [];
      this.updateTable();
      this.searching = true;


      this.attachmentPaginator.firstPage();
      let paginator = this.paginatorAttachmentSbj.getValue();
      this.attachmentService.getAttachmentsCount(query).pipe(takeUntil(this.destroy$)).subscribe(res => {
        this.attachmentsCount = res;
      })

      this.attachmentService.searchAttachments(this.currentSearch, { ...query, offset: paginator.pageIndex * paginator.pageSize, limit: paginator.pageSize })
        .pipe(takeUntil(this.destroy$)).subscribe(data => {
          //this.isLoadingAttachements = false;
          this.handleAttachments(data);
          this.searching = false;
        })

    } else {

      this.attachmentService.getAttachmentsCount(query).pipe(takeUntil(this.destroy$)).subscribe(res => {
        this.attachmentsCount = res;
      })

      let paginator = this.paginatorAttachmentSbj.getValue();

      this.attachmentService.getAttachments({ ...query, offset: paginator.pageIndex * paginator.pageSize, limit: paginator.pageSize })
        .pipe(takeUntil(this.destroy$)).subscribe(data => {
          // this.isLoadingAttachements = false;
          this.handleAttachments(data);
        })
    }
  }

  handleAttachments(attachments: any[]) {
    attachments.forEach((att: any) => {
      att.date = format(new Date(att.createdDate), "yyyy/MM/dd HH:mm:ss");
      att.predictionArray = [];
      if (att.predictions)
        att.predictions.forEach((p: any) => {
          if (p.probability > .5) {
            att.predictionArray.push({ tag: p.tagName || p.name, probability: (p.probability * 100).toFixed(0) + "%" })
          }
        })
    })
    this.attachments = attachments;
    this.updateTable();
  }

  updateTable() {
    switch (this.tabIndex) {
      case 0:
        if (!this.eventsData)
          this.eventsData = new MatTableDataSource(this.events);
        else
          this.eventsData.data = this.events;
        break;
      case 1:
        if (!this.metricsData)
          this.metricsData = new MatTableDataSource(this.metrics);
        else {
          this.metricsData.data = [];
          this.metricsData.data = this.metrics;
        }
        break;
      case 2:
        if (!this.attachmentsData)
          this.attachmentsData = new MatTableDataSource(this.attachments);
        else
          this.attachmentsData.data = this.attachments;
        break;
    }
  }

  initSocket() {
    this.socketEventSub = this.socket.getUpdate("Event").pipe(takeUntil(this.destroy$)).subscribe((events: any[]) => {
      if (isBefore(new Date(this.endDate), subHours(new Date(), 1))) return;
      events.forEach((e: any) => {
        e = this.parseEvent(e);
        e.initTitle = e.title;
        let data = this.eventsData.data;
        data.push(e);
        data.sort((a: any, b: any) => differenceInMilliseconds(new Date(a.startDate), new Date(b.startDate)) < 0 ? 1 : -1)
        this.eventsData.data = data;
      });
      this.updateTable();
    })
    // this.socket.getMetrics().then(socket => {
    //   this.socketMetricSub = socket.pipe(takeUntil(this.destroy$)).subscribe(events => {

    //     if(isBefore(new Date(this.endDate), subHours(new Date(), 1))) return;
    //     events.forEach(e => {

    //       e = this.parseMetric(e);
    //       e.initTitle = e.title;
    //       let data = this.metricsData.data;
    //       data.push(e);
    //       data.sort((a, b) => differenceInMilliseconds(new Date(a.startDate), new Date(b.startDate)) < 0 ? 1 : -1)
    //       this.metricsData.data = data;

    //     });
    //     this.updateTable();

    //   })
    // })
    this.socketAttachmentSub = this.socket.getUpdate("Attachment").pipe(takeUntil(this.destroy$)).subscribe(async (attachments: Attachment[]) => {
      if (isBefore(new Date(this.endDate), subHours(new Date(), 1))) return;

      try {
        attachments.sort((a: Attachment, b: Attachment) => differenceInMilliseconds(new Date(a.createdDate), new Date(b.createdDate)) > 0 ? 1 : -1)
        this.attachments = this.attachments.concat(attachments);
        if (this.attachments && this.attachments.length) {
          this.events.forEach((event: any) => {
            event.attachments = [];
            if (event.startDate && event.endDate) {
              this.attachments.forEach(a => {
                let tracker = (a.tracker as Tracker)?.name ? (a.tracker as Tracker).name : a.tracker;
                let unit = (a.unit as Unit)?.name ? (a.unit as Unit).name : a.unit;
                if (isWithinInterval(new Date(a.createdDate), { start: new Date(event.startDate), end: new Date(event.endDate) }) &&
                  ((event.trackerName && event.trackerName == tracker) || (event.unitName && event.unitName == unit))) {
                  event.attachments?.push(a);
                }
              })
            }
          })

          this.updateTable();
        }

      } catch (e) {
        console.error(e)
      }
    })
  }

  applyFilter(event: any) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.eventsData.filter = filterValue.trim().toLowerCase();

    if (this.eventsData.paginator) {
      this.eventsData.paginator.firstPage();
    }
  }

  replayEvent(event: Event, type: string) {
    if (event.endDate) {

      this.router.navigate([type], { queryParams: { mode: 'replay', metricsOnly: false, play: true, startDate: event.startDate, endDate: event.endDate } })
    } else {

      this.router.navigate([type], { queryParams: { mode: 'replay', metricsOnly: false, play: true, startDate: subSeconds(new Date(event.startDate), 10).toISOString(), endDate: addSeconds(new Date(event.startDate), 10).toISOString() } })
    }
  }

  onTabChanged(event: number) {
    // if (this.trackerTypes.length == 0) {
    //   return;
    // }
    this.tabIndex = event;
    // if (this.eventsData)
    //   this.eventPaginator.firstPage();
    // if (this.metricsData) {
    //   this.metricPaginator.firstPage();
    // }
    // if (this.attachmentsData)
    //   this.attachmentPaginator.firstPage();
    switch (this.tabIndex) {
      case 0:
        // this.eventPaginator.firstPage();
        this.getEvents();
        break;
      case 1:
        // this.metricPaginator.firstPage();
        this.getMetrics();
        break;
      case 2:
        // this.attachmentPaginator.firstPage();
        this.getAttachments();
        break;
    }
    const queryParams: Params = { tab: event };
    this.router.navigate(
      [],
      {
        queryParams: queryParams,
        queryParamsHandling: 'merge', // remove to replace all query params by provided
      });
  }

  onImageClick(index: number, attachments: Attachment[]) {
    let attachment = attachments[index];

    if (!attachment) return;

    const dialogRef = this.dialog.open(GalleryComponent, {
      width: "auto",
      height: "auto",
      data: {
        index,
        id: (attachment as any)._id || attachment.id,
        urls: attachments.map(a => a.url),
        startDate: attachment.createdDate,
        endDate: formatISO(addMinutes(new Date(attachment.createdDate), 1)),
        boxes: (attachment as any).predictions,
        user: this.authService.currentUserValue,
        tags: attachment.tags || [],
        tagDisplayConfig: this.tagDisplayConfig
      }
    });

    dialogRef.afterClosed().subscribe((res) => { });
  }

  onEventChanged(event: any) {
    delete event.editable;
    this.eventService.updateEvent(event).subscribe(res => { })
  }

  getPayloadItemConfig(key: string): { icon?: string; svgIcon?: string; color: string } {
    return this.payloadIconConfig[key] || { icon: 'data_object', color: 'muted' };
  }

  isObject(value: any): boolean {
    return typeof value === 'object' && value !== null;
  }

  getObjectDetails(obj: any): { key: string; value: string }[] {
    if (!obj || typeof obj !== 'object') {
      return [];
    }
    return Object.entries(obj).map(([key, value]) => ({
      key,
      value: String(value)
    }));
  }
}
